import React, { useEffect, useMemo, useState } from 'react';
import CurrencyPicker from '../../travel/components/CurrencyPicker';
import currencyService from '../../../services/currencyServiceWeb';

interface Props {
  title: string;
  amount: string;
  currency: string;
  date: string;
  onTitleChange: (v: string) => void;
  onAmountChange: (v: string) => void;
  onCurrencyChange: (v: string) => void;
  onDateChange: (v: string) => void;
  manualRate?: string;
  onManualRateChange?: (v: string) => void;
  isManualRate?: boolean;
  convertedAmount?: string;
  // notify parent of numeric converted amount in target currency (or null)
  onConvertedChange?: (value: number | null) => void;
  // explicit target currency to convert to (e.g., travel currency). If omitted,
  // falls back to language-based default in the currency service.
  targetCurrency?: string | null;
}

const ReceiptBasicInfoWeb: React.FC<Props> = ({ title, amount, currency, date, onTitleChange, onAmountChange, onCurrencyChange, onDateChange, manualRate, onManualRateChange, isManualRate, convertedAmount, targetCurrency, onConvertedChange }) => {
  const [convertedPreview, setConvertedPreview] = useState<string | null>(null);
  const [rateInfo, setRateInfo] = useState<string | null>(null);

  // compute conversion when amount or currency changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!amount) {
          setConvertedPreview(null);
          setRateInfo(null);
          return;
        }
        const n = Number((amount || '').toString().replace(/,/g, '').trim());
        if (isNaN(n)) {
          setConvertedPreview(null);
          setRateInfo(null);
          return;
        }

        // Prefer an explicit target currency (passed from parent) such as the
        // travel's currency. Fall back to language-based default if not provided.
  const defaultCurrency = targetCurrency || currencyService.getDefaultCurrencyForLanguage((navigator as any)?.language || undefined) || 'CNY';

        // If same currency, treat converted amount as the same numeric value
        if (currency === defaultCurrency) {
          const formatted = `${currencyService.formatAmount(n, defaultCurrency, true)}`;
          setConvertedPreview(formatted);
          setRateInfo(null);
          if (typeof onConvertedChange === 'function') {
            // notify parent with numeric converted amount (keep null for invalid)
            onConvertedChange(Number(n));
          }
          return;
        }

        // show optimistic sync conversion using cache first
        const sync = currencyService.convertAmountSync(n, currency, defaultCurrency);
        setConvertedPreview(`${currencyService.formatAmount(sync, defaultCurrency, true)}`);
  if (typeof onConvertedChange === 'function') onConvertedChange(Number(sync));
        try {
          const rate = await currencyService.getExchangeRate(currency, defaultCurrency);
          if (cancelled) return;
          const converted = n * rate;
          setConvertedPreview(`${currencyService.formatAmount(converted, defaultCurrency, true)}`);
          setRateInfo(`1 ${currency} = ${Number(rate).toFixed(4)} ${defaultCurrency}`);
          if (typeof onConvertedChange === 'function') onConvertedChange(Number(converted));
        } catch (e) {
          // leave sync value and show fallback note
          if (!cancelled) setRateInfo('Exchange rate unavailable');
        }
      } catch (e) {
        if (!cancelled) {
          setConvertedPreview(null);
          setRateInfo(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [amount, currency, targetCurrency]);

  return (
    <div style={styles.card}>
      <div style={styles.title}>Basic Information</div>

      <div style={styles.field}>
        <label style={styles.label}>Receipt Title *</label>
        <input value={title} onChange={(e) => onTitleChange(e.target.value)} placeholder="e.g. Lunch expense" style={styles.input} />
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Amount *</label>
        <input value={amount} onChange={(e) => onAmountChange(e.target.value)} placeholder="0.00" style={{ ...styles.input }} />
        {convertedPreview && <div style={{ marginTop: 8, color: '#6b7280' }}>≈ {convertedPreview}</div>}
        {rateInfo && <div style={{ marginTop: 4, color: '#9CA3AF', fontSize: 13 }}>{rateInfo}</div>}
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Currency</label>
        <div>
          <CurrencyPicker value={currency} onChange={onCurrencyChange} compact={false} showFlag={true} />
        </div>
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Expense Date</label>
        <input type="date" value={date} onChange={(e) => onDateChange(e.target.value)} style={styles.input} />
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Description</label>
        <textarea placeholder="Add notes..." style={{ ...styles.input, height: 80 }} />
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  card: { background: '#fff', padding: 20, borderRadius: 12, marginBottom: 16 },
  title: { fontSize: 18, fontWeight: 700, marginBottom: 12 },
  field: { marginBottom: 12, paddingLeft: 20, paddingRight: 20, boxSizing: 'border-box' },
  label: { display: 'block', fontWeight: 600, marginBottom: 6 },
  input: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, boxSizing: 'border-box' },
  row: { display: 'flex', gap: 12, alignItems: 'center' },
  select: { minWidth: 120, padding: '10px 8px', borderRadius: 8, border: '1px solid #e5e7eb' },
  manualRate: { marginTop: 8, display: 'flex', alignItems: 'center' },
  rateInput: { width: 120, padding: '6px 8px', borderRadius: 6, border: '1px solid #e5e7eb' },
};

export default ReceiptBasicInfoWeb;
